import * as LZString from 'lz-string';
import State from "../../State";
import ProjectionSave from "./ProjectionSave";
import StateSave from "./StateSave";
import TotalsSave from "./TotalsSave";
import GameTimeSave from './GameTimeSave';
import ValuesSave from './ValuesSave';
import MinedUpgradeCollectionSave from './MinedUpgradeCollectionSave';
import CrewSave from './CrewSave';
import PartyTargetSave from './PartyTargetSave';
import GridSaveManager from './GridSaveManager';
import GridSaveManagerSave from './GridSaveManagerSave';
import PickUpgradeCollectionSave from './PickUpgradeCollectionSave';
import StatisticsSave from './StatisticsSave';
import SkillTreeSave from './SkillTreeSave';
import DamageSave from './DamageSave';
import BonusCollectionSave from './BonusCollectionSave';
import AutomationSave from './AutomationSave';
import GoldUpgradeCollectionSave from './GoldUpgradeCollectionSave';
import MineControlSave from './MineControlSave';
import GameOptionsSave from './GameOptionsSave';

export default class SaveManager {
    /**
     * 
     * @param {GridSaveManager} gridSaveManager
     */
    constructor(gridSaveManager) {

        /**
         * @private
         * @constant
         * @type {GridSaveManager}
         */
        this.gridSaveManager = gridSaveManager;

        /**
         * @constant
         * @type {String}
         */
        this.modelsSaveKey = 'modelsSaveV1';


        /////////////////////////////////////////
        // localStorage.clear();
        // console.log('CLEARED LOCAL STORAGE!!!');
        /////////////////////////////////////////
    }


    /**
     * @param {State} state 
     * @returns {boolean} True if the save file was loaded.
     */
    loadSave(state) {
        return this.loadModels(state);
    }

    /**
     * 
     * @param {State} state 
     */
    saveGame(state) {
        this.saveAllLoadedGrids(state);

        // Save the models after the grids so that we can save the grid save manager.
        this.saveModels(state);
    }
    
    deleteSave() {
        localStorage.removeItem(this.modelsSaveKey);
    
        this.gridSaveManager.deleteAllGrids();
    }

    /**
     * @private
     * @param {State} state 
     */
    saveModels(state) {
        const modelState = this.generateModelState(state);
        if (!modelState) {
            console.log('SaveManager.saveModels() Failed to generate models save state');
            return;
        }

        const modelStateJsonString = JSON.stringify(modelState); // This is super fast.  Each grid is roughly 47,000 characters

        if (!modelStateJsonString) {
            console.log('SaveManager.saveModels() Failed to stringify models state');
            return;
        }

        const compressedStateString = LZString.compressToUTF16(modelStateJsonString);
        if (!compressedStateString) {
            console.log('SaveManager.saveModels() Failed to compress state for models');
            return;            
        }
    
        localStorage.setItem(this.modelsSaveKey, compressedStateString);
    }

    /**
     * @private
     * @param {State} state 
     * @returns {boolean} True if the models were loaded from the save file.
     */
    loadModels(state) {

        const compressedStateString = localStorage.getItem(this.modelsSaveKey);

        if (!compressedStateString) {
            console.log('SaveManager.loadModels() No save found.');
            return false;
        }

        const modelStateJsonString = LZString.decompressFromUTF16(compressedStateString);
        if (!modelStateJsonString) {
            console.log('SaveManager.loadModels() Failed to decompress save.');
            return false;
        }

        const modelState = JSON.parse(modelStateJsonString);
        if (!modelState) {
            console.log('SaveManager.loadModels() Failed to parse save as JSON.');
            return false;
        }

        return this.loadModelState(state, modelState);
    }

    /**
     * @private
     * @param {State} state 
     * @returns {Object}
     */
    generateModelState(state) {
        const modelState = {};

        modelState.state = StateSave.generateSave(state);
        modelState.gridSaveManager = GridSaveManagerSave.generateSave(state.gridSaveManager)
        modelState.time = GameTimeSave.generateSave(state.gameTime);
        modelState.projection = ProjectionSave.generateSave(state.projection);
        modelState.partyTarget = PartyTargetSave.generateSave(state.partyTarget);
        modelState.gameOptions = GameOptionsSave.generateSave(state.gameOptions);
        modelState.crew = CrewSave.generateSave(state.crew);
        modelState.totals = TotalsSave.generateSave(state.totals);
        modelState.damage = DamageSave.generateSave(state.damage);
        modelState.values = ValuesSave.generateSave(state.values);
        modelState.runStats = StatisticsSave.generateSave(state.runStatistics);
        modelState.globalStats = StatisticsSave.generateSave(state.globalStatistics);
        modelState.minedUpgradeCollection = MinedUpgradeCollectionSave.generateSave(state.minedUpgradeCollection);
        modelState.pickUpgradeCollection = PickUpgradeCollectionSave.generateSave(state.pickUpgradeCollection);
        modelState.goldUpgradeCollection = GoldUpgradeCollectionSave.generateSave(state.goldUpgradeCollection);
        modelState.bonusCollection = BonusCollectionSave.generateSave(state.bonusCollection);
        modelState.automation = AutomationSave.generateSave(state.automationManager);
        modelState.mineControl = MineControlSave.generateSave(state.mineControl);
        modelState.skillTree = SkillTreeSave.generateSave(state.skillTreeGraph);

        return modelState;
    }

    /**
     * @private
     * @param {State} state 
     * @param {Object} modelState 
     * @returns {boolean}
     */
    loadModelState(state, modelState) {
        if (!modelState) {
            return false;
        }

        let success = true;

        try {
            StateSave.loadSave(state, modelState.state);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load State:', error.message);
            success = false;
        }

        try {
            GridSaveManagerSave.loadSave(state.gridSaveManager, modelState.gridSaveManager);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load GridSaveManager:', error.message);
            success = false;
        }

        try {
            GameTimeSave.loadSave(state.gameTime, modelState.time);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load GameTime:', error.message);
            success = false;
        }

        try {
            ProjectionSave.loadSave(state.projection, modelState.projection);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load Projection:', error.message);
            success = false;
        }

        try {
            CrewSave.loadSave(state.crew, modelState.crew);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load Crew:', error.message);
            success = false;
        }

        try {
            TotalsSave.loadSave(state.totals, modelState.totals);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load Totals:', error.message);
            success = false;
        }

        try {
            DamageSave.loadSave(state.damage, modelState.damage);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load Damage:', error.message);
            success = false;
        }

        try {
            ValuesSave.loadSave(state.values, modelState.values);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load Values:', error.message);
            success = false;
        }

        try {
            StatisticsSave.loadSave(state.runStatistics, modelState.runStats);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load Statistics (run):', error.message);
            success = false;
        }

        try {
            StatisticsSave.loadSave(state.globalStatistics, modelState.globalStats);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load Statistics (global):', error.message);
            success = false;
        }

        try {
            PartyTargetSave.loadSave(state.partyTarget, modelState.partyTarget);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load PartyTarget:', error.message);
            success = false;
        }

        try {
            GameOptionsSave.loadSave(state.gameOptions, modelState.gameOptions);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load PartyTarget:', error.message);
            success = false;
        }

        try {
            MinedUpgradeCollectionSave.loadSave(state.minedUpgradeCollection, state.upgradeCreators, modelState.minedUpgradeCollection);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load MinedUpgradeCollection:', error.message);
            success = false;
        }

        try {
            PickUpgradeCollectionSave.loadSave(state.pickUpgradeCollection, modelState.pickUpgradeCollection);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load PickUpgradeCollection:', error.message);
            success = false;
        }

        try {
            GoldUpgradeCollectionSave.loadSave(state.goldUpgradeCollection, modelState.goldUpgradeCollection);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load GoldUpgradeCollection:', error.message);
            success = false;
        }

        try {
            BonusCollectionSave.loadSave(state.bonusCollection, modelState.bonusCollection);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load BonusCollection:', error.message);
            success = false;
        }

        try {
            AutomationSave.loadSave(state.automationManager, modelState.automation);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load AutomationSave:', error.message);
            success = false;
        }

        try {
            MineControlSave.loadSave(state.mineControl, modelState.mineControl);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load MineControlSave:', error.message);
            success = false;
        }

        try {
            SkillTreeSave.loadSave(state.skillTreeGraph, modelState.skillTree);
        }
        catch (error) {
            console.error('SaveManager.loadModelState() Failed to load SkillTreeSave:', error.message);
            success = false;
        }

        return success;
    }

    /**
     * @private
     * @param {State} state 
     */
    saveAllLoadedGrids(state) {
        const grids = state.worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const grid = grids[i];
            if (grid && grid.isLoaded()) {
                this.gridSaveManager.saveGridState(grid);
            }
        }
    }

    /**
     * @param {State} state 
     * @returns {string}
     */
    getSaveStateForExport(state) {
        
        const modelState = this.generateModelState(state);
        if (!modelState) {
            console.log('SaveManager.getSaveState() Failed to generate models save state');
            return null;            
        }

        const gridState = this.gridSaveManager.generateGridStateForExport(state.worldGrid);
        if (!gridState) {
            console.log('SaveManager.getSaveState() Failed to generate grid state');
            // Lets just continue
        }

        // gridState looks like this:
        // const worldGridState = {
        //     grids: []
        // };


        // This combines the model state and the grid state.
        const saveState = {
            model: modelState,
            grid: gridState
        };

        const saveStateJsonString = JSON.stringify(saveState); // This is super fast.  Each grid is roughly 47,000 characters

        if (!saveStateJsonString) {
            console.log('SaveManager.getSaveState() Failed to stringify state');
            return null;            
        }

        const compressedStateString = LZString.compressToBase64(saveStateJsonString);
        if (!compressedStateString) {
            console.log('SaveManager.getSaveState() Failed to compress state');
            return null;
        }

        return compressedStateString;
    }

    /**
     * @param {State} state 
     * @param {string} saveStateString 
     * @returns {boolean}
     */
    importSave(state, saveStateString) {
        if (!saveStateString) {
            console.log('SaveManager.importSave() No save found.');
            return false;
        }

        const saveStateJsonString = LZString.decompressFromBase64(saveStateString);
        if (!saveStateJsonString) {
            console.log('SaveManager.importSave() Failed to decompress save.');
            return false;
        }

        const saveState = JSON.parse(saveStateJsonString);
        if (!saveState) {
            console.log('SaveManager.importSave() Failed to parse save as JSON.');
            return false;
        }

        // The saveState object should look like this:
        //
        // const saveState = {
        //     model: modelState,
        //     grid: gridState
        // };

        const modelState = saveState.model;
        const worldGridState = saveState.grid;

        const modelStateLoaded = this.loadModelState(state, modelState);
        console.log('SaveManager.importSave() load model state:' + modelStateLoaded);

        // Don't mess with the grid state we have failed to load the model state.
        if (!modelStateLoaded) {
            return false;
        }

        // The grid state looks like this:
        //
        // const worldGridState = {
        //     grids: []
        // };

        /*
        LOOK AT THE LOGIC IN GridLoader.loadGrid(): 
        I don't think the grid xy origin is in the save file.
        I also don't think we're initializing the zones for the grid.

            // Sanity check.
            if (grid.isLoaded()) {
                this.unloadGrid(grid);
            }

            // Update origin
            grid.origin.set(x, y);

            // Initialize all the grid zones/regions/tiles for the new grid origin.
            grid.initializeZonesForGrid(); // <-- updates the origins of all zones/regions/tiles.
        */

        // Before we import the new grid state, we need to clear out the old stuff.
        this.gridSaveManager.deleteAllGrids();

        const worldGridStateLoaded = this.gridSaveManager.importGridState(state.worldGrid, worldGridState);
        console.log('SaveManager.importSave() import grid state:' + worldGridStateLoaded);

        if (worldGridStateLoaded) {
            console.log('SaveManager.importSave() saving newly imported state.')

            const grids = state.worldGrid.grids;
            for (let i = 0; i < grids.length; i++) {
                const grid = grids[i];
                if (grid) {
                    console.log('SaveManager.importSave() SAVING GRID STATE.');
                    this.gridSaveManager.saveGridState(grid);
                }
            }
    
            // Save the models after the grids so that we can save the grid save manager.
            this.saveModels(state);

        }
        else {
            console.log('SaveManager.importSave() FAIL modelStateLoaded=' + modelStateLoaded + ' worldGridStateLoaded=' + worldGridStateLoaded);
        }

        return worldGridStateLoaded;
    }
}